import React, { useState, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CustomerEngagementProps } from "../../types";
import FeedbackSelection from "./FeedbackSelection";
import FeedbackModal from "./FeedbackModal";
import Submit from "./Submit";
import { TextArea } from "../Common/styles";
import { useMutation } from "@apollo/client";
import { CaptureConsumerEngagementFeedbacks } from "../../graphql/mutation";
import DialogModal from "../Common/DialogModal";
import { getThankYouMessage, updateSessionData } from "./utils";

const CustomerEngagement = ({
  customerScanDetails,
  sessionId,
  traceItemID,
}: CustomerEngagementProps) => {
  const [rating, setRating] = useState<number>(0);
  const [thumbs, setThumbs] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [successOpen, setSuccessOpen] = useState<boolean>(false);
  const [thankYouMessage, setThankYouMessage] = useState<string>("");

  const noFeedbackModal = customerScanDetails.authMethod === "anonymous" && customerScanDetails.formId?.length === 0;

  // Feedback mutation
  const [CreateConsumerEngagement] = useMutation(
    CaptureConsumerEngagementFeedbacks
  );

  useEffect(() => {
    const sessionData = JSON.parse(localStorage.getItem("sessionData") || "{}");
    const feedbackSubmitted = sessionData[traceItemID]?.feedbackSubmitted;
    if (feedbackSubmitted) {
      setIsSubmitted(true);
    }
  }, [sessionId, traceItemID]);

  const handleStarClick = (index) => {
    setRating(index + 1);
    if (!noFeedbackModal) {
      setOpen(true);
    }
  };

  const handleThumbClick = (value) => {
    setThumbs(value);
    if (!noFeedbackModal) {
      setOpen(true);
    }
  };

  const handleCommentChange = (event) => {
    if (event.target.value.length > 100) {
      setError("Comments should not exceed 100 characters.");
    } else {
      setError("");
      setComment(event.target.value);
    }
  };

  const handleSubmit = async () => {
    if (customerScanDetails.commentsFiled === "mandatoryOnLowRating") {
      if (
        (rating > 0 && rating <= 2 && !comment) ||
        (thumbs === "thumbsdown" && !comment)
      ) {
        setError("Please tell us more.");
        return;
      }
    }
    setError("");
    setLoading(true);

    try {
      const inputData = {
        sessionId: sessionId,
        consumerEngagementDefId: customerScanDetails.id,
        rating:
          customerScanDetails.feedbackMethod === "rating"
            ? rating.toString()
            : thumbs || "",
        ...(comment && { comments: comment }),
      };

      await CreateConsumerEngagement({
        variables: {
          consumerEngagementFeedbackInput: inputData,
        },
      });

      setLoading(false);
      
      // Get thank you message using the shared utility function
      const message = getThankYouMessage(
        customerScanDetails.feedbackMethod,
        rating,
        thumbs,
        customerScanDetails.submissionDisplayMessage
      );
      
      setThankYouMessage(message);
      setSuccessOpen(true);

      // Update session data using the shared utility function
      updateSessionData(traceItemID);
      
      setRating(0);
      setThumbs(null);
      setComment("");
      setIsSubmitted(true);
    } catch (error) {
      setLoading(false);
      console.error("Error submitting feedback:", error);
    }
  };

  const handleDialogClose = () => {
    setSuccessOpen(false);
  };

  return (
    <>
      {!isSubmitted && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Typography variant="h5" sx={{ mt: 2, textAlign: "center" }}>
            We love your feedback
          </Typography>
          <FeedbackSelection
            feedbackMethod={customerScanDetails.feedbackMethod}
            rating={rating}
            thumbs={thumbs}
            handleStarClick={handleStarClick}
            handleThumbClick={handleThumbClick}
          />
          {noFeedbackModal && (
            <>
              <TextArea
                minRows={3}
                placeholder={`Please tell us more`}
                name="comments"
                value={comment}
                onChange={handleCommentChange}
                sx={{ width: "300px", mt: 1, resize: "none" }}
              />
              {error && (
                <Typography color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
              <Submit 
                isFormValid={rating > 0 || thumbs !== null}
                loading={loading} 
                handleSubmit={handleSubmit}
              />
            </>
          )}
        </Box>
      )}
      {isSubmitted && !open && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Thank you for your feedback!
          </Typography>
          <CheckCircleIcon sx={{ color: "green", fontSize: 25, ml: 1 }} />
        </Box>
      )}
      {!noFeedbackModal && (
        <FeedbackModal
          open={open}
          setOpen={setOpen}
          customerScanDetails={customerScanDetails}
          rating={rating}
          setRating={setRating}
          thumbs={thumbs}
          setThumbs={setThumbs}
          sessionId={sessionId}
          traceItemID={traceItemID}
          isSubmitted={isSubmitted}
          setIsSubmitted={setIsSubmitted}
        />
      )}
      <DialogModal
        open={successOpen}
        setOpen={setSuccessOpen}
        message={thankYouMessage}
        title="Thank you for your feedback!"
        onClose={handleDialogClose}
      />
    </>
  );
};

export default CustomerEngagement;
